import { Phone, MessageCircle, ArrowRight, Home as HomeIcon, ChevronRight, Check, ShieldCheck, Award, HelpCircle, ChevronDown, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import BackToTop from "../components/BackToTop";
import ContactForm from "../components/ContactForm";
import PriceHint from "../components/PriceHint";
import { PEST_CITY_LINK_BY_NAME } from "../lib/pestCities";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { SITE } from "../lib/site";

/**
 * Reusable SEO-first pillar page for pest control landing pages.
 * All content passed via props to keep pages DRY.
 */
export default function PestPillarLayout({
  slug,
  seoTitle,
  seoDescription,
  breadcrumbLabel,
  heroBadge,
  heroTitleHtml,
  heroSubtitle,
  hero_h2,
  introParagraphs = [],
  problems = [],
  method = [],
  faq = [],
  ctaTitle,
  cities = [],
  relatedArticles = [
    { title: "Punaises de lit : reconnaître, traiter, éviter", slug: "punaises-de-lit-reconnaitre-traiter-eviter" },
    { title: "Différence guêpe, frelon européen, frelon asiatique", slug: "difference-guepe-frelon-frelon-asiatique" },
  ],
  jsonLdExtra,
  whatsappText,
  withQuoteForm = false,
  quoteServiceIndex,
  quoteIntro,
}) {
  const canonical = `https://kreuse-exterieur.fr/${slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: seoTitle,
        description: seoDescription,
        url: canonical,
        serviceType: breadcrumbLabel,
        areaServed: [
          { "@type": "AdministrativeArea", name: "Creuse (23)" },
          { "@type": "AdministrativeArea", name: "Haute-Vienne (87)" },
          { "@type": "AdministrativeArea", name: "Indre (36)" },
          { "@type": "AdministrativeArea", name: "Allier (03)" },
        ],
        provider: {
          "@type": ["LocalBusiness", "PestControl"],
          "@id": "https://kreuse-exterieur.fr/#business",
          name: "K'REUSE EXTÉRIEUR",
          url: "https://kreuse-exterieur.fr/",
          telephone: "+33780541115",
          image: SITE.logo,
          priceRange: "€€",
          paymentAccepted: "Espèces, Chèque, Virement bancaire",
          address: {
            "@type": "PostalAddress",
            streetAddress: "27 rue des Pradelles",
            addressLocality: "La Celle-Dunoise",
            postalCode: "23800",
            addressRegion: "Creuse",
            addressCountry: "FR",
          },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: breadcrumbLabel, item: canonical },
        ],
      },
      ...(faq.length > 0
        ? [
            {
              "@type": "FAQPage",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
      ...(jsonLdExtra ? [jsonLdExtra] : []),
    ],
  };

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical,
    jsonLd,
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-14 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#FFE0E0] opacity-50 blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium">{breadcrumbLabel}</span>
            </nav>

            {heroBadge && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B5E20]/10 text-[#1B5E20] text-xs font-semibold tracking-wide uppercase mb-6">
                <ShieldCheck className="w-4 h-4" />
                {heroBadge}
              </div>
            )}

            <h1
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2E1C] tracking-tight leading-[1.05] max-w-4xl"
              dangerouslySetInnerHTML={{ __html: heroTitleHtml }}
            />

            {heroSubtitle && (
              <p className="mt-6 text-lg text-[#5C705E] max-w-3xl leading-relaxed">{heroSubtitle}</p>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:+33780541115" data-testid="pillar-cta-call">
                <Button className="rounded-full bg-[#1B5E20] hover:bg-[#154018] text-white px-8 h-14 text-base">
                  <Phone className="w-4 h-4 mr-2" />
                  07 80 54 11 15
                </Button>
              </a>
              {withQuoteForm && (
                <a
                  href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(whatsappText || "Bonjour, je souhaite un devis.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pillar-cta-whatsapp"
                >
                  <Button className="rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-8 h-14 text-base">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              )}
              {withQuoteForm ? (
                <a href="#contact" data-testid="pillar-cta-quote">
                  <Button variant="outline" className="rounded-full border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white px-8 h-14 text-base">
                    Devis gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              ) : (
                <Link to="/#contact" data-testid="pillar-cta-quote">
                  <Button variant="outline" className="rounded-full border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white px-8 h-14 text-base">
                    Devis gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>

            <PriceHint slug={slug} />

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-[#5C705E]">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[#1B5E20]" />
                Certifié Certibiocide
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#1B5E20]" />
                25 avis 5,0/5 Google
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#1B5E20]" />
                Creuse (23) + limitrophes
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        {(hero_h2 || introParagraphs.length > 0) && (
          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              {hero_h2 && (
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight mb-8">
                  {hero_h2}
                </h2>
              )}
              <div className="prose prose-lg max-w-none text-[#3D4D3F] leading-relaxed space-y-5">
                {introParagraphs.map((p, i) => (
                  <p key={`intro-${i}`}>{p}</p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Problems / Sub-sections */}
        {problems.length > 0 && (
          <section className="py-14 md:py-20 bg-[#F8FBF8]">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {problems.map((p, i) => (
                  <div
                    key={p.title}
                    data-testid={`pillar-problem-${i}`}
                    className="bg-white rounded-2xl p-6 border border-[#E5EBE5] hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] text-lg mb-3">
                      {p.title}
                    </h3>
                    <p className="text-sm text-[#5C705E] leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Method */}
        {method.length > 0 && (
          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight mb-10 text-center">
                Notre méthode en <span className="font-medium text-[#1B5E20]">{method.length} étapes</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {method.map((s, i) => (
                  <div key={s.title} className="relative">
                    <div className="text-5xl font-['Outfit'] font-light text-[#1B5E20]/20 mb-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] text-base mb-2">{s.title}</h3>
                    <p className="text-sm text-[#5C705E] leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Cities */}
        {cities.length > 0 && (
          <section className="py-14 md:py-20 bg-[#F8FBF8]">
            <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight mb-6">
                Nous intervenons dans toute la <span className="font-medium text-[#1B5E20]">Creuse</span>
              </h2>
              <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
                {cities.map((c) => {
                  const href = PEST_CITY_LINK_BY_NAME[c];
                  return href ? (
                    <Link
                      key={c}
                      to={href}
                      data-testid={`pillar-city-link-${c}`}
                      className="inline-block bg-white border border-[#E5EBE5] text-[#1B5E20] text-sm px-4 py-2 rounded-full hover:bg-[#1B5E20] hover:text-white transition-colors"
                    >
                      {c}
                    </Link>
                  ) : (
                    <span
                      key={c}
                      className="inline-block bg-white border border-[#E5EBE5] text-[#1B5E20] text-sm px-4 py-2 rounded-full"
                    >
                      {c}
                    </span>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-white border-t border-[#E5EBE5]">
            <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-4">
                À lire aussi
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedArticles.slice(0, 2).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/conseils/${a.slug}`}
                    data-testid={`pillar-related-${a.slug}`}
                    className="group block p-5 bg-[#F8FBF8] rounded-2xl border border-[#E5EBE5] hover:border-[#1B5E20] hover:shadow-md transition-all text-left"
                  >
                    <div className="text-xs text-[#5C705E] mb-1">Conseil</div>
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] group-hover:text-[#1B5E20] transition-colors">
                      {a.title}
                    </h3>
                    <div className="mt-2 text-sm text-[#1B5E20] inline-flex items-center gap-1">
                      Lire l'article <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="py-16 md:py-20 bg-gradient-to-b from-transparent to-[#E8F5E9]/40" data-testid="pillar-faq-section">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                  <HelpCircle className="w-4 h-4" />
                  Questions fréquentes
                </div>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2E1C] tracking-tight">
                  Vos <span className="font-medium text-[#1B5E20]">questions</span>
                </h2>
              </div>
              <div className="space-y-3">
                {faq.map((item, i) => (
                  <details
                    key={item.q}
                    data-testid={`pillar-faq-${i}`}
                    className="group bg-white rounded-2xl border border-[#E5EBE5] hover:shadow-md transition-shadow overflow-hidden"
                  >
                    <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
                      <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] text-base sm:text-lg">
                        {item.q}
                      </h3>
                      <ChevronDown className="w-5 h-5 text-[#1B5E20] flex-shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 -mt-1 text-sm text-[#5C705E] leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {withQuoteForm && <ContactForm defaultServiceIndex={quoteServiceIndex} introText={quoteIntro} />}

        {/* CTA */}
        <section className="py-16 md:py-24 bg-[#1B5E20] text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-4">
              {ctaTitle || "Un problème de nuisibles ? On agit."}
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Devis gratuit sous 24h. Certifié Certibiocide. Interventions 7j/7 en urgence.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="tel:+33780541115" data-testid="pillar-cta-bottom-call">
                <Button className="rounded-full bg-white text-[#1B5E20] hover:bg-white/90 px-8 h-14 text-base font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  07 80 54 11 15
                </Button>
              </a>
              <Link to={withQuoteForm ? `/${slug}#contact` : "/#contact"} data-testid="pillar-cta-bottom-quote">
                <Button variant="outline" className="rounded-full border-white bg-transparent text-white hover:bg-white hover:text-[#1B5E20] px-8 h-14 text-base">
                  Devis gratuit
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton text={whatsappText} />
      <BackToTop />
    </div>
  );
}

export function ProblemsList({ children }) {
  return <ul className="space-y-2 mt-4 text-sm text-[#3D4D3F]">{children}</ul>;
}

export function ProblemItem({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="w-4 h-4 text-[#1B5E20] flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}
